import ColumnManagementPopover from '@/components/DynamicPages/ColumnManagementPopover.tsx';
import { useHasFeature } from '@/hooks/useHasFeature.ts';
import NewEditorModal from '@/modals/NewEditorModal';
import {
  getAnalyticsByDates,
  getExternalAnalyticsByDates,
} from '@/services/AnalyticsService.ts';
import {
  getExperimentsStatsForDynamicPage,
  updateExperiment,
  updateVariant,
} from '@/services/DynamicPageVariantsService.ts';
import { analyticsRangeToDates, buildPreviewUrl, capitalize } from '@/utils';
import { getSchedulesString } from '@/utils/constraints.utils.ts';
import { getWeightInPercentage } from '@/utils/variant-utils.ts';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import {
  AgentRunAttachment,
  DynamicPageVariant,
  VariantPrompt,
  Website,
} from '@superfunnel/types';
import AlertModal from '@superfunnel/ui-core/AlertModal';
import Loader from '@superfunnel/ui-core/Loader';
import Badge from '@superfunnel/ui-core/v2/Badge';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { classNames } from '@superfunnel/utils';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import {
  Clock,
  Divide,
  EditPencil,
  Link,
  LongArrowDownRight,
  OpenNewWindow,
  Sparks,
} from 'iconoir-react';
import Lottie from 'lottie-react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import DynamicStats from '../../components/analytics/DynamicStats';
import { StatCellValues } from '../../components/analytics/StatCellValues';
import { StatsGridItem } from '../../components/analytics/StatsGrid';
import {
  CustomDataGrid,
  CustomDataGridHandle,
} from '../../components/CustomDataGrid/CustomDataGrid';
import {
  AnalyticsRange,
  DashboardContext,
} from '../../contexts/DashboardContext';
import { useDynamicPageContext } from '../../contexts/DynamicPageContext';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext, useUserContext } from '../../contexts/UserContext';

import { VariantOriginResult } from '@/modals/VariantOriginModal/types';
import { useVariantOriginData } from '@/modals/VariantOriginModal/useVariantOriginData';
import VariantOriginModal from '@/modals/VariantOriginModal/VariantOriginModal';
import EditVariantConstraintsModal from '../../modals/EditVariantConstraintsModal';
import EditVariantNameAndDescriptionModal from '../../modals/EditVariantNameAndDescriptionModal';
import ExperimentModal from '../../modals/ExperimentModal';
import {
  copyWebsiteDevelopment,
  createNewDynamicPageVariant,
} from '../../services/DynamicPagesService';
import {
  VariantRowActions,
  type DynamicPageVariantsRowData,
} from './components/VariantRowActions';

const testsChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const getRowStatus = (row: DynamicPageVariantsRowData) => {
  if (row.type === 'test') {
    return row.test.data().status === 'creating' ? 'creating' : 'active';
  } else {
    return row.tests.length &&
      row.tests.every((t) => t.data().status === 'creating')
      ? 'creating'
      : row.variant.data().status;
  }
};

export default function ManageDynamicPageDetails() {
  const tableRef = useRef<CustomDataGridHandle<any>>(null);
  const { show, hide } = useContext(SnackbarContext);
  const hasFinancialDataFeature = useHasFeature('viewFinancialData');
  const {
    selectedAccount,
    analyticsCustomEvents,
    analyticsExternalPixelEvents,
  } = useContext(UserContext);
  const { analyticsRange, analyticsType } = useContext(DashboardContext);
  const { selectedDynamicPage, variants, websites, customRangeAnalytics } =
    useDynamicPageContext();

  const [adsAccountAnalytics, setAdsAccountAnalytics] = useState<Record<
    string,
    any
  > | null>();
  const [, setIsLoadingAdsAccountAnalytics] = useState(false);

  const [websiteBuildingAnimation, setWebsiteBuildingAnimation] =
    useState<any>(null);
  const [variantOriginModalState, setVariantOriginModalState] = useState<{
    open: boolean;
    mode: 'variant' | 'test';
    /** Target website ID (for test mode) */
    targetWebsiteId?: string;
    /** Target variant ID */
    targetVariantId?: string;
    /** Control website ID for test mode (first websiteId of the variant) */
    controlWebsiteId?: string;
    /** Default prompt from the variant (from agent conversation) */
    defaultPrompt?: VariantPrompt | null;
  }>({ open: false, mode: 'variant' });
  const [openEditor, setOpenEditor] = useState<{
    websiteId: string;
    variantId: string;
    initialMessage?:
      | string
      | { content: string; attachments?: AgentRunAttachment[] };
    initialShowAnalytics?: boolean;
  }>();
  const [editVariantConstraintsModelOpen, setEditVariantConstraintsModelOpen] =
    useState<{ variantId: string }>();
  const [editVariantScheduleModelOpen, setEditVariantScheduleModelOpen] =
    useState<{ variantId: string }>();
  const [editNameAndDescriptionModalOpen, setEditNameAndDescriptionModalOpen] =
    useState<{
      variantId: string;
      initialName: string | undefined;
      initialDescription: string | undefined;
      initialPath: string | undefined;
    }>();

  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] =
    useState<{
      entity: string;
      name: string;
      onConfirm: () => void | Promise<void>;
    }>();

  const [selectedGoogleConversionAction, setSelectedGoogleConversionAction] =
    useState<string | null>(null);

  const [experimentModalOpen, setExperimentModalOpen] = useState<{
    mode: 'create' | 'edit';
    variantId: string;
    variantName: string | undefined;
    experimentId?: string;
  }>();

  // Fetch all dynamic pages with variants for the origin modal
  const { dynamicPagesWithVariants, isLoading: isLoadingDynamicPages } =
    useVariantOriginData();

  // Track if we're creating a variant from the origin modal
  const [isCreatingVariant, setIsCreatingVariant] = useState(false);

  // Handler for variant origin selection
  const handleVariantOriginSelect = useCallback(
    async (result: VariantOriginResult) => {
      if (!selectedAccount || !selectedDynamicPage) return;

      // Handle test mode - copy content and open editor
      if (variantOriginModalState.mode === 'test') {
        const targetWebsiteId = variantOriginModalState.targetWebsiteId;
        const targetVariantId = variantOriginModalState.targetVariantId;

        if (!targetWebsiteId || !targetVariantId) {
          show('Missing target information', 'warn');
          return;
        }

        // Get the test description for the fallback message
        const targetWebsite = websites?.find((w) => w.id === targetWebsiteId);
        const testDescription =
          targetWebsite?.data().name || targetWebsite?.data().description;
        const fallbackMessage = testDescription
          ? `Update the page to test: ${testDescription}.`
          : 'Create a fully working page';

        // Helper to build initialMessage with optional attachments
        const buildInitialMessage = (content: string) => {
          if (result.attachments && result.attachments.length > 0) {
            return { content, attachments: result.attachments };
          }
          return content;
        };

        // If empty, just open the editor with the prompt
        if (result.originType === 'empty') {
          setOpenEditor({
            websiteId: targetWebsiteId,
            variantId: targetVariantId,
            initialMessage: buildInitialMessage(
              result.prompt || fallbackMessage,
            ),
          });
          setVariantOriginModalState((prev) => ({ ...prev, open: false }));
          return;
        }

        // For controlVariation or copying from another variant, copy content first
        if (
          (result.originType === 'controlVariation' ||
            result.originType === 'existingVariant' ||
            result.originType === 'differentDynamicPage') &&
          result.selectedVariant
        ) {
          try {
            await copyWebsiteDevelopment(
              selectedAccount.id,
              result.selectedVariant.dynamicPageId,
              result.selectedVariant.websiteId,
              selectedDynamicPage.id,
              targetWebsiteId,
            );

            // Open editor with the prompt
            setOpenEditor({
              websiteId: targetWebsiteId,
              variantId: targetVariantId,
              initialMessage: buildInitialMessage(
                result.prompt || fallbackMessage,
              ),
            });
            setVariantOriginModalState((prev) => ({ ...prev, open: false }));
          } catch (error) {
            console.error('Error copying content:', error);
            show('Failed to copy content', 'warn');
          }
          return;
        }

        return;
      }

      // Handle variant mode - create a new variant
      setIsCreatingVariant(true);
      const snackbar = show('Creating variant...', 'info', {
        disableAutoClose: true,
      });

      // Helper to build initialMessage with optional attachments
      const buildInitialMessage = (content: string) => {
        if (result.attachments && result.attachments.length > 0) {
          return { content, attachments: result.attachments };
        }
        return content;
      };

      try {
        // Create the new variant
        const { variantId, websiteId } = await createNewDynamicPageVariant(
          selectedAccount.id,
          selectedDynamicPage.id,
          'New Variant', // Default name
          undefined, // Description
          {}, // No constraints initially
        );

        // If copying from another variant, copy the development data
        if (
          (result.originType === 'existingVariant' ||
            result.originType === 'differentDynamicPage') &&
          result.selectedVariant
        ) {
          hide(snackbar.id);

          try {
            await copyWebsiteDevelopment(
              selectedAccount.id,
              result.selectedVariant.dynamicPageId,
              result.selectedVariant.websiteId,
              selectedDynamicPage.id,
              websiteId,
            );
          } catch (copyError) {
            console.error('Error copying content:', copyError);
            // Continue anyway - variant was created, just without copied content
          }
        } else {
          hide(snackbar.id);
        }

        // show('Variant created successfully', 'info');
        setVariantOriginModalState((prev) => ({ ...prev, open: false }));

        // Open the editor right away
        setOpenEditor({
          websiteId,
          variantId,
          initialMessage: buildInitialMessage(
            result.prompt || 'Create a fully working page',
          ),
        });
      } catch (error) {
        console.error('Error creating variant:', error);
        hide(snackbar.id);
        show('Failed to create variant', 'warn');
      } finally {
        setIsCreatingVariant(false);
      }
    },
    [
      selectedAccount,
      selectedDynamicPage,
      variantOriginModalState.mode,
      variantOriginModalState.targetWebsiteId,
      variantOriginModalState.targetVariantId,
      websites,
      show,
      hide,
    ],
  );

  // State to store previous period analytics data
  const [previousPeriodAnalytics, setPreviousPeriodAnalytics] = useState<any>();
  const [previousPeriodAdsAnalytics, setPreviousPeriodAdsAnalytics] =
    useState<any>();

  // Check if any variants have running experiments
  const hasRunningExperiments = useMemo(() => {
    return variants?.some((v) => v.data().currentExperimentId);
  }, [variants]);

  // Fetch experiment statistics for all running experiments
  const { data: experimentsStats } = useQuery({
    queryKey: [
      'experimentsStats',
      selectedAccount?.id,
      selectedDynamicPage?.id,
    ],
    queryFn: async () => {
      if (!selectedAccount?.id || !selectedDynamicPage?.id) return {};
      return getExperimentsStatsForDynamicPage(
        selectedAccount.id,
        selectedDynamicPage.id,
      );
    },
    enabled:
      !!selectedAccount?.id &&
      !!selectedDynamicPage?.id &&
      hasRunningExperiments,
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000, // Consider data stale after 30 seconds
  });

  // Load Lottie animation
  useEffect(() => {
    import('../../assets/lotties/website-building.json').then((module) =>
      setWebsiteBuildingAnimation(module.default),
    );
  }, []);

  // Helper function to calculate previous period date range
  const getPreviousPeriodDates = useCallback(
    (analyticsRange: AnalyticsRange) => {
      if (typeof analyticsRange === 'string') {
        const today = new Date();

        switch (analyticsRange) {
          case 'today': {
            // Previous period is yesterday
            const startDate = new Date(today);
            startDate.setDate(startDate.getDate() - 1);
            const endDate = new Date(today);
            endDate.setDate(endDate.getDate() - 1);
            return {
              startDate: startDate.toISOString().split('T')[0],
              endDate: endDate.toISOString().split('T')[0],
            };
          }
          case 'yesterday': {
            // Previous period is day before yesterday
            const startDate = new Date(today);
            startDate.setDate(startDate.getDate() - 2);
            const endDate = new Date(today);
            endDate.setDate(endDate.getDate() - 2);
            return {
              startDate: startDate.toISOString().split('T')[0],
              endDate: endDate.toISOString().split('T')[0],
            };
          }
          case 'last7Days': {
            // Previous period is the 7 days before the current 7 days
            const endDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            const startDate = new Date(
              endDate.getTime() - 7 * 24 * 60 * 60 * 1000,
            );
            return {
              startDate: startDate.toISOString().split('T')[0],
              endDate: endDate.toISOString().split('T')[0],
            };
          }
          case 'last30Days': {
            // Previous period is the 30 days before the current 30 days
            const endDate = new Date(
              today.getTime() - 30 * 24 * 60 * 60 * 1000,
            );
            const startDate = new Date(
              endDate.getTime() - 30 * 24 * 60 * 60 * 1000,
            );
            return {
              startDate: startDate.toISOString().split('T')[0],
              endDate: endDate.toISOString().split('T')[0],
            };
          }
          case 'currentMonth': {
            // Previous period is the previous month
            const currentMonth = new Date(
              today.getFullYear(),
              today.getMonth(),
              1,
            );
            const previousMonthEnd = new Date(currentMonth.getTime() - 1);
            const previousMonthStart = new Date(
              previousMonthEnd.getFullYear(),
              previousMonthEnd.getMonth(),
              1,
            );
            return {
              startDate: previousMonthStart.toISOString().split('T')[0],
              endDate: previousMonthEnd.toISOString().split('T')[0],
            };
          }
          default:
            return null;
        }
      } else if (
        analyticsRange &&
        analyticsRange.startDate &&
        analyticsRange.endDate
      ) {
        // For custom ranges, calculate the previous period of the same length
        const startDate = new Date(analyticsRange.startDate);
        const endDate = new Date(analyticsRange.endDate);
        const rangeDuration = endDate.getTime() - startDate.getTime();

        const previousEndDate = new Date(
          startDate.getTime() - 24 * 60 * 60 * 1000,
        ); // Day before start
        const previousStartDate = new Date(
          previousEndDate.getTime() - rangeDuration,
        );

        return {
          startDate: previousStartDate.toISOString().split('T')[0],
          endDate: previousEndDate.toISOString().split('T')[0],
        };
      }

      return null;
    },
    [],
  );

  // Fetch previous period analytics data
  useEffect(() => {
    if (!selectedAccount || !selectedDynamicPage || !analyticsRange) {
      setPreviousPeriodAnalytics(undefined);
      return;
    }

    const previousPeriodDates = getPreviousPeriodDates(analyticsRange);
    if (!previousPeriodDates) {
      setPreviousPeriodAnalytics(null);
      return;
    }

    let relevant = true;
    getAnalyticsByDates(
      selectedAccount.id,
      'campaign',
      selectedDynamicPage.id,
      previousPeriodDates.startDate,
      previousPeriodDates.endDate,
    )
      .then((res) => {
        if (!relevant) return;
        setPreviousPeriodAnalytics(res);
      })
      .catch((error) => {
        if (!relevant) return;
        console.error('Error fetching previous period analytics:', error);
        setPreviousPeriodAnalytics(null);
      });

    return () => {
      relevant = false;
    };
  }, [
    selectedAccount,
    selectedDynamicPage,
    analyticsRange,
    getPreviousPeriodDates,
  ]);

  const stats = useMemo((): StatsGridItem[] => {
    if (openEditor) {
      return [];
    }

    const analyticsData = customRangeAnalytics;
    const analyticsKey = 'custom';

    // Helper function to calculate percentage change
    const calculateChange = (
      current: number,
      previous: number,
    ): { change: string; changeType: 'positive' | 'negative' } => {
      if (previous === 0) {
        return current > 0
          ? { change: '+100%', changeType: 'positive' }
          : { change: '0%', changeType: 'positive' };
      }
      const percentageChange = ((current - previous) / previous) * 100;
      const isPositive = percentageChange >= 0;
      return {
        change: `${isPositive ? '+' : ''}${percentageChange.toFixed(1)}%`,
        changeType: isPositive ? 'positive' : 'negative',
      };
    };

    // Get current period data
    const current = {
      visitors: analyticsData?.uniqueVisitors?.custom ?? 0,
      conversions: analyticsData?.uniqueConversions?.custom ?? 0,
    };

    // Get previous period data
    const previous = previousPeriodAnalytics
      ? {
          visitors: previousPeriodAnalytics?.uniqueVisitors?.custom ?? 0,
          conversions: previousPeriodAnalytics?.uniqueConversions?.custom ?? 0,
        }
      : { visitors: 0, conversions: 0 };

    // Calculate comparison values only if we have previous period data
    const hasValidComparison = !!previousPeriodAnalytics;

    const currentConversionRate =
      current.visitors > 0 ? (current.conversions / current.visitors) * 100 : 0;
    const previousConversionRate =
      previous.visitors > 0
        ? (previous.conversions / previous.visitors) * 100
        : 0;

    const conversionRateChange = hasValidComparison
      ? calculateChange(currentConversionRate, previousConversionRate)
      : undefined;

    const visitorsChange = hasValidComparison
      ? calculateChange(current.visitors, previous.visitors)
      : undefined;

    const conversionsChange = hasValidComparison
      ? calculateChange(current.conversions, previous.conversions)
      : undefined;

    return [
      {
        id: 'conversion-rate',
        name: 'Conv. Rate',
        value: (
          <StatCellValues
            analyticsType={analyticsType}
            analyticsKey={analyticsKey}
            analyticsData={analyticsData}
            format={(attrs) => (attrs.visitors ? `${attrs.cr}%` : '-')}
            showAll
          />
        ),
        change: conversionRateChange?.change,
        changeType: conversionRateChange?.changeType,
      },
      {
        id: 'visitors',
        name: 'Visitors',
        value: (
          <StatCellValues
            analyticsType={analyticsType}
            analyticsKey={analyticsKey}
            analyticsData={analyticsData}
            format={(attrs) => attrs.visitors ?? '-'}
            showAll
          />
        ),
        change: visitorsChange?.change,
        changeType: visitorsChange?.changeType,
      },
      {
        id: 'conversions',
        name: 'Conversions',
        value: (
          <StatCellValues
            analyticsType={analyticsType}
            analyticsKey={analyticsKey}
            analyticsData={analyticsData}
            format={(attrs) => attrs.conversions ?? '-'}
            showAll
          />
        ),
        change: conversionsChange?.change,
        changeType: conversionsChange?.changeType,
      },

      {
        id: 'active-variants',
        name: 'Active Variants',
        value: websites?.filter((w) => w.data().status === 'active')?.length
          ? websites?.filter((w) => w.data().status === 'active')?.length
          : '-',
      },
    ];
  }, [
    openEditor,
    customRangeAnalytics,
    previousPeriodAnalytics,
    analyticsType,
    websites,
  ]);

  useEffect(() => {
    if (!selectedAccount || !selectedDynamicPage || !analyticsRange) return;
    if (!Object.keys(selectedDynamicPage.data().connections ?? {}).length) {
      setAdsAccountAnalytics(null);
      return;
    }

    const { startDate, endDate } = analyticsRangeToDates(analyticsRange);

    setIsLoadingAdsAccountAnalytics(true);
    setAdsAccountAnalytics(undefined);
    getExternalAnalyticsByDates(
      selectedAccount.id,
      selectedDynamicPage.id,
      startDate,
      endDate,
    )
      .then((res) => {
        setAdsAccountAnalytics(res || null);
      })
      .finally(() => {
        setIsLoadingAdsAccountAnalytics(false);
      });
  }, [analyticsRange, selectedDynamicPage, selectedAccount]);

  // Fetch previous period ads analytics data
  useEffect(() => {
    if (!selectedAccount || !selectedDynamicPage || !analyticsRange) {
      setPreviousPeriodAdsAnalytics(undefined);
      return;
    }

    if (!Object.keys(selectedDynamicPage.data().connections ?? {}).length) {
      setPreviousPeriodAdsAnalytics(null);
      return;
    }

    const previousPeriodDates = getPreviousPeriodDates(analyticsRange);
    if (!previousPeriodDates) {
      setPreviousPeriodAdsAnalytics(null);
      return;
    }

    let relevant = true;
    getExternalAnalyticsByDates(
      selectedAccount.id,
      selectedDynamicPage.id,
      previousPeriodDates.startDate,
      previousPeriodDates.endDate,
    )
      .then((res) => {
        if (!relevant) return;
        setPreviousPeriodAdsAnalytics(res || null);
      })
      .catch((error) => {
        if (!relevant) return;
        console.error('Error fetching previous period ads analytics:', error);
        setPreviousPeriodAdsAnalytics(null);
      });

    return () => {
      relevant = false;
    };
  }, [
    selectedAccount,
    selectedDynamicPage,
    analyticsRange,
    getPreviousPeriodDates,
  ]);

  const adAccountStats = useMemo((): StatsGridItem[] => {
    if (openEditor) {
      return [];
    }
    const currencyFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: adsAccountAnalytics?.currencyCode || 'USD',
    });
    const numberFormatter = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 1,
    });
    const percentageFormatter = new Intl.NumberFormat('en-US', {
      style: 'percent',
    });

    const loading = (
      <div className="h-2.5 w-1/2 max-w-12 min-w-7 animate-pulse rounded-md bg-gray-300"></div>
    );

    // Helper function to calculate percentage change for ads data
    const calculateAdsChange = (
      current: number,
      previous: number,
    ): { change: string; changeType: 'positive' | 'negative' } => {
      if (previous === 0) {
        return current > 0
          ? { change: '+100%', changeType: 'positive' }
          : { change: '0%', changeType: 'positive' };
      }
      const percentageChange = ((current - previous) / previous) * 100;
      const isPositive = percentageChange >= 0;
      return {
        change: `${isPositive ? '+' : ''}${percentageChange.toFixed(1)}%`,
        changeType: isPositive ? 'positive' : 'negative',
      };
    };

    // Check if we have valid comparison data
    const hasValidAdsComparison = !!previousPeriodAdsAnalytics;

    const selectedGoogleConversion = selectedGoogleConversionAction
      ? adsAccountAnalytics?.conversionsPerActionArray?.find(
          (c: any) => c.actionName === selectedGoogleConversionAction,
        )
      : null;

    const previousSelectedGoogleConversion =
      selectedGoogleConversionAction && previousPeriodAdsAnalytics
        ? previousPeriodAdsAnalytics?.conversionsPerActionArray?.find(
            (c: any) => c.actionName === selectedGoogleConversionAction,
          )
        : null;

    // Calculate changes for each metric
    const cpcChange =
      hasValidAdsComparison && hasFinancialDataFeature
        ? calculateAdsChange(
            adsAccountAnalytics?.['cpc'] ?? 0,
            previousPeriodAdsAnalytics?.['cpc'] ?? 0,
          )
        : undefined;

    const clicksChange = hasValidAdsComparison
      ? calculateAdsChange(
          adsAccountAnalytics?.['clicks'] ?? 0,
          previousPeriodAdsAnalytics?.['clicks'] ?? 0,
        )
      : undefined;

    const conversionsChange = hasValidAdsComparison
      ? calculateAdsChange(
          selectedGoogleConversionAction
            ? (selectedGoogleConversion?.conversions ?? 0)
            : (adsAccountAnalytics?.['conversions'] ?? 0),
          selectedGoogleConversionAction
            ? (previousSelectedGoogleConversion?.conversions ?? 0)
            : (previousPeriodAdsAnalytics?.['conversions'] ?? 0),
        )
      : undefined;

    return [
      ...(hasFinancialDataFeature
        ? [
            {
              id: 'cpc',
              name: 'CPC (avg)',
              change: cpcChange?.change,
              changeType: cpcChange?.changeType,
              value:
                adsAccountAnalytics !== undefined
                  ? adsAccountAnalytics?.['cpc']
                    ? currencyFormatter.format(
                        Math.ceil((adsAccountAnalytics?.['cpc'] ?? 0) * 100) /
                          100,
                      )
                    : '-'
                  : loading,
            },
          ]
        : []),
      {
        id: 'ad-clicks',
        name: 'Ad Clicks',
        value:
          adsAccountAnalytics !== undefined
            ? adsAccountAnalytics?.['clicks']
              ? numberFormatter.format(adsAccountAnalytics?.['clicks'])
              : '-'
            : loading,
        change: clicksChange?.change,
        changeType: clicksChange?.changeType,
      },
      {
        id: 'google-conversions',
        name: (
          <span>
            <DropdownMenu>
              <DropdownMenuTrigger
                disabled={
                  !adsAccountAnalytics?.conversionsPerActionArray?.length
                }
              >
                <div className="flex w-full flex-row items-start justify-between gap-2">
                  <span className="truncate sm:max-w-36">
                    {selectedGoogleConversionAction || 'Google Conversions'}
                  </span>
                  {(!!adsAccountAnalytics?.conversionsPerActionArray?.length ||
                    selectedGoogleConversionAction) && (
                    <ChevronDownIcon className="mt-1 size-4" />
                  )}
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent side={'bottom'} align={'end'}>
                <DropdownMenuItem
                  onClick={() => {
                    setSelectedGoogleConversionAction(null);
                  }}
                >
                  All
                </DropdownMenuItem>
                {adsAccountAnalytics?.conversionsPerActionArray?.map(
                  (c: any) => (
                    <DropdownMenuItem
                      key={c.actionName}
                      onClick={() => {
                        setSelectedGoogleConversionAction(c.actionName);
                      }}
                    >
                      {c.actionName}
                    </DropdownMenuItem>
                  ),
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </span>
        ),
        value:
          adsAccountAnalytics !== undefined
            ? selectedGoogleConversionAction
              ? selectedGoogleConversion
                ? numberFormatter.format(selectedGoogleConversion.conversions)
                : '-'
              : adsAccountAnalytics?.['conversions']
                ? numberFormatter.format(adsAccountAnalytics?.['conversions'])
                : '-'
            : loading,
        change: conversionsChange?.change,
        changeType: conversionsChange?.changeType,
      },
      {
        id: 'avgSpeedScore',
        name: 'Page Speed Score',
        value: adsAccountAnalytics?.['avgSpeedScore']
          ? percentageFormatter.format(
              Math.ceil((adsAccountAnalytics?.['avgSpeedScore'] ?? 0) * 100),
            )
          : '-',
        // No change calculation for speed score as it's not a comparable metric
      },
    ];
  }, [
    adsAccountAnalytics,
    previousPeriodAdsAnalytics,
    hasFinancialDataFeature,
    openEditor,
    selectedGoogleConversionAction,
  ]);

  const onDeleteVariantButtonClick = useCallback(
    (variant: QueryDocumentSnapshot<DynamicPageVariant>) => {
      setDeleteConfirmationModalOpen({
        entity: 'variant',
        name: variant.data().name,
        onConfirm: async () => {
          if (!selectedAccount || !selectedDynamicPage) return;
          updateVariant(
            selectedAccount.id,
            selectedDynamicPage.id,
            variant.id,
            { status: 'deleted' },
          );
        },
      });
    },
    [selectedAccount, selectedDynamicPage],
  );

  const onDeleteTestButtonClick = useCallback(
    (
      test: QueryDocumentSnapshot<Website>,
      variant: QueryDocumentSnapshot<DynamicPageVariant>,
    ) => {
      const variantData = variant.data();
      const experimentId = variantData.currentExperimentId;
      if (!experimentId) return;

      setDeleteConfirmationModalOpen({
        entity: 'test',
        name: test.data().name || 'Test',
        onConfirm: async () => {
          updateExperiment(
            selectedAccount?.id ?? '',
            selectedDynamicPage?.id ?? '',
            variant.id,
            experimentId,
            {
              websiteIdsToRemove: [test.id],
            },
          );
        },
      });
    },
    [selectedAccount?.id, selectedDynamicPage?.id],
  );

  const getItemMetadataCached = useMemo(() => {
    const _cache = new Map<string, ReturnType<typeof _getMetadata>>();

    const _getMetadata = (row: DynamicPageVariantsRowData) => {
      const analyticsData = customRangeAnalytics;

      const status = getRowStatus(row);

      const websiteAnalytics =
        status === 'creating'
          ? undefined
          : analyticsData
            ? row.type === 'test'
              ? (analyticsData?.test?.[row.test.id] ?? {})
              : (analyticsData?.variant?.[row.variant.id] ?? {})
            : undefined;

      const analyticsKey = 'custom';

      return {
        websiteAnalytics,
        analyticsKey,
        analyticsData,
      };
    };

    return (row: DynamicPageVariantsRowData) => {
      const key =
        row.type + '-' + (row.type === 'test' ? row.test.id : row.variant.id);
      const existing = _cache.get(key);
      if (!existing) {
        const md = _getMetadata(row);
        _cache.set(key, md);
        return md;
      }

      return existing;
    };
  }, [customRangeAnalytics]);

  const rows: DynamicPageVariantsRowData[] = useMemo(() => {
    const websitesMap = new Map<string, QueryDocumentSnapshot<Website>>(
      websites?.map((w) => [w.id, w]) || [],
    );
    return (
      variants
        ?.map((variant) => {
          const tests = variant
            .data()
            .websiteIds.map((wid) => websitesMap.get(wid))
            .filter(
              (w): w is QueryDocumentSnapshot<Website> => w !== undefined,
            );

          return [
            { variant, type: 'variant', tests },
            ...(tests.length > 1
              ? tests.map((test, i) => ({
                  type: 'test' as const,
                  variant,
                  test,
                  testIndex: i,
                  tests,
                }))
              : []),
          ] satisfies DynamicPageVariantsRowData[];
        })
        .flat() ?? []
    );
  }, [variants, websites]);

  const columns: ColumnDef<DynamicPageVariantsRowData>[] = useMemo(() => {
    if (openEditor) {
      return [];
    }
    return [
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) =>
          row.type === 'variant'
            ? row.variant.data().name
            : row.test.data().name,
        size: 240,
        cell: ({ row: { original } }) => {
          const percentageFormatter = new Intl.NumberFormat('en-US', {
            style: 'percent',
            maximumFractionDigits: 2,
          });

          const weightInPercentage =
            original.type === 'test'
              ? getWeightInPercentage(original.variant.data(), original.test.id)
              : 0;

          return (
            <>
              {original.type === 'variant' && (
                <div className="w-full flex-col">
                  <div className="flex w-full flex-row items-center gap-1.5">
                    <div className="group relative flex max-w-full items-center gap-1">
                      <div className="truncate font-medium text-gray-900">
                        {original.variant.data().name}
                      </div>

                      {original.variant.data().status !== 'draft' &&
                        original.tests.length > 0 && (
                          <OpenInNewWindowButtonMenu
                            variantId={original.variant.id}
                            websiteId={original.tests[0].id}
                          />
                        )}

                      <Button
                        size={'sm'}
                        className="absolute -end-8 hidden group-hover/row:block"
                        variant="ghost"
                        onClick={() => {
                          setEditNameAndDescriptionModalOpen({
                            variantId: original.variant.id,
                            initialName:
                              original.variant.data().name ?? undefined,
                            initialDescription:
                              original.variant.data().description ?? undefined,
                            initialPath:
                              original.variant.data().path ?? undefined,
                          });
                        }}
                      >
                        <EditPencil className="text-base-900 h-4.5 w-4.5" />
                        <span className="sr-only">, Edit</span>
                      </Button>
                    </div>
                  </div>
                  {original.variant.data().description && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className="max-w-44 overflow-hidden font-medium text-ellipsis whitespace-nowrap text-gray-500"
                          onClick={() => {
                            setEditNameAndDescriptionModalOpen({
                              variantId: original.variant.id,
                              initialName:
                                original.variant.data().name ?? undefined,
                              initialDescription:
                                original.variant.data().description ??
                                undefined,
                              initialPath:
                                original.variant.data().path ?? undefined,
                            });
                          }}
                        >
                          {original.variant.data().description}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p>{original.variant.data().description}</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {original.variant.data().path && (
                    <div className="text-xs text-gray-400">
                      /v/{original.variant.data().path}
                    </div>
                  )}
                </div>
              )}
              {original.type === 'test' && (
                <div className="flex w-full max-w-72 flex-col justify-between gap-1.5 pt-1">
                  <div className="flex w-full flex-row items-center gap-3">
                    <div className="-mt-2.5 h-4">
                      <LongArrowDownRight className="text-base-900 h-[18] w-[18]" />
                    </div>
                    <div
                      className="group relative flex cursor-pointer items-center"
                      onClick={() => {
                        const experimentId =
                          original.variant.data().currentExperimentId;
                        if (experimentId) {
                          setExperimentModalOpen({
                            mode: 'edit',
                            variantId: original.variant.id,
                            variantName: original.variant.data().name,
                            experimentId,
                          });
                        }
                      }}
                    >
                      <span className="w-10">
                        {percentageFormatter.format(weightInPercentage / 100)}
                      </span>

                      {/* <Button
                        size={'sm'}
                        className="absolute -end-4 hidden group-hover:block"
                        variant="ghost"
                        onClick={() => {}}
                      >
                        <EditPencil className="text-base-900 h-4.5 w-4.5" />
                        <span className="sr-only">, Edit</span>
                      </Button> */}
                    </div>
                    <Badge
                      variant={original.testIndex !== 0 ? 'warning' : 'purple'}
                      className="h-6 shrink-0"
                    >
                      {original.testIndex === 0
                        ? 'Control'
                        : original.tests.length === 2
                          ? 'Experiment'
                          : `Experiment ${testsChars[original.testIndex - 1] ?? testsChars[testsChars.length - 1]}`}
                    </Badge>
                    {/* <div className="ms-0 flex flex-col items-start justify-start gap-2">
                      <span
                        className={classNames(
                          'inline-flex items-center rounded-md bg-blue-50 px-2 py-1.5 text-xs font-medium text-blue-700 capitalize ring-1 ring-green-600/20 ring-inset',
                          original.testIndex !== 0
                            ? 'bg-orange-50 text-orange-700'
                            : '',
                        )}
                      >
                        {original.testIndex === 0
                          ? 'Original'
                          : original.tests.length === 2
                            ? 'Experiment'
                            : `Experiment ${testsChars[original.testIndex - 1] ?? testsChars[testsChars.length - 1]}`}
                      </span>
                    </div> */}
                    {original.variant.data().status !== 'draft' && (
                      <OpenInNewWindowButtonMenu
                        variantId={original.variant.id}
                        websiteId={original.test.id}
                      />
                    )}
                  </div>
                  <div className="flex w-full flex-col gap-1 ps-8">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className="w-fit max-w-72 cursor-pointer overflow-hidden font-medium text-ellipsis whitespace-nowrap text-gray-500 hover:text-gray-700"
                          onClick={() => {
                            const experimentId =
                              original.variant.data().currentExperimentId;
                            if (experimentId) {
                              setExperimentModalOpen({
                                mode: 'edit',
                                variantId: original.variant.id,
                                variantName: original.variant.data().name,
                                experimentId,
                              });
                            }
                          }}
                        >
                          {original.test.data().name}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p>{original.test.data()?.name || ''}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              )}
            </>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => {
          return getRowStatus(row);
        },
        size: 140,
        cell: ({ row: { original } }) => {
          const status = getRowStatus(original);
          if (original.type === 'test') return <></>;
          const isDraft = status === 'draft';
          const hasSchedules =
            original.type === 'variant'
              ? !!original.variant.data().constraints?.schedules?.length
              : false;
          return (
            // <Badge
            //   title={
            //     status === 'active'
            //       ? 'Active'
            //       : status === 'creating'
            //         ? 'Generating'
            //         : 'Inactive'
            //   }
            //   rounded={false}
            //   hideDot
            //   showBorder
            //   color={
            //     status === 'active'
            //       ? 'green'
            //       : status === 'creating'
            //         ? 'blue'
            //         : 'gray'
            //   }
            //   size="normal"
            // />
            <div className="flex items-center gap-1">
              <Badge
                variant={
                  status === 'draft'
                    ? 'secondary'
                    : status === 'active'
                      ? 'success'
                      : status === 'creating'
                        ? 'default'
                        : 'secondary'
                }
              >
                {capitalize(
                  status === 'draft'
                    ? 'Draft'
                    : status === 'active'
                      ? 'Active'
                      : status === 'creating'
                        ? 'Generating'
                        : 'Inactive',
                )}
              </Badge>
              {hasSchedules && !isDraft && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="hover:bg-base-200"
                      onClick={() => {
                        setEditVariantScheduleModelOpen({
                          variantId: original.variant.id,
                        });
                      }}
                    >
                      <Clock className="text-base-400 size-[18px]" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>
                      {getSchedulesString(
                        original.variant.data().constraints!.schedules![0],
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        id: 'itemId',
        header: 'ID',
        accessorFn: (row) => {
          return row.type === 'variant' ? row.variant.id : row.test.id;
        },
        size: 200,
      },
      {
        id: 'constraints',
        header: 'Smart Traffic',
        accessorKey: 'constraints',
        size: 210,
        cell: ({ row: { original } }) => {
          if (original.type === 'test') return <></>;
          const { constraints } = original.variant.data();

          const hasConstraints = constraints
            ? Object.entries(constraints).some(
                ([k, v]) => k !== 'schedules' && v.length,
              )
            : false;

          return (
            <Button
              className={classNames(
                'text-base-900 font-inter h-7 text-[13px] font-medium -tracking-[0.01em]',
                hasConstraints
                  ? 'bg-brand-50 border-brand-50 text-brand-800'
                  : '',
              )}
              variant="outline"
              size="sm"
              onClick={() => {
                setEditVariantConstraintsModelOpen({
                  variantId: original.variant.id,
                });
              }}
            >
              <Divide className="size-4.5" />
              {hasConstraints ? 'Edit Rules' : 'Set Rules'}
            </Button>
          );
        },
      },

      {
        id: 'visitors',
        header: 'Visitors',
        accessorKey: 'analyticsData.visitors',
        size: 120,
        cell: ({ row: { original } }) => {
          const { analyticsKey, websiteAnalytics } =
            getItemMetadataCached(original);
          return (
            <StatCellValues
              analyticsType={analyticsType}
              analyticsKey={analyticsKey}
              analyticsData={websiteAnalytics}
              format={(attrs) => attrs.visitors ?? '-'}
              showAll
              fill
            />
          );
        },
      },
      {
        id: 'conversions',
        header: 'Conversions',
        accessorKey: 'analyticsData.conversions',
        size: 120,
        cell: ({ row: { original } }) => {
          const { websiteAnalytics, analyticsKey } =
            getItemMetadataCached(original);
          return (
            <StatCellValues
              analyticsType={analyticsType}
              analyticsKey={analyticsKey}
              analyticsData={websiteAnalytics}
              format={(attrs) => attrs.conversions ?? '-'}
              showAll
              fill
            />
          );
        },
      },
      {
        id: 'cr',
        header: 'Conversion rate',
        accessorKey: 'campaignExtra.cr',
        size: 170,
        cell: ({ row: { original } }) => {
          const { websiteAnalytics, analyticsKey } =
            getItemMetadataCached(original);
          return (
            <StatCellValues
              analyticsType={analyticsType}
              analyticsKey={analyticsKey}
              analyticsData={websiteAnalytics}
              format={(attrs) => (attrs.visitors ? `${attrs.cr}%` : '-')}
              showAll
              fill
            />
          );
        },
      },
      {
        id: 'buttonClicks',
        header: 'Button Clicks',
        accessorKey: 'analyticsData.buttonClicks',
        size: 170,
        cell: ({ row: { original } }) => {
          const { websiteAnalytics, analyticsKey } =
            getItemMetadataCached(original);
          return (
            <StatCellValues
              analyticsType={analyticsType}
              analyticsKey={analyticsKey}
              analyticsData={websiteAnalytics}
              format={(attrs) => attrs.buttonClicks ?? '-'}
              showAll
              fill
            />
          );
        },
      },
      {
        id: 'uniqueLinkClicks',
        header: 'Unique link clicks',
        accessorKey: 'analyticsData.uniqueButtonClicks',
        size: 170,
        cell: ({ row: { original } }) => {
          const { analyticsKey, websiteAnalytics } =
            getItemMetadataCached(original);
          return (
            <StatCellValues
              analyticsType={analyticsType}
              analyticsKey={analyticsKey}
              analyticsData={websiteAnalytics}
              format={(attrs) => {
                const val = attrs.uniqueButtonClicks;
                const percentageFormatter = new Intl.NumberFormat('en-US', {
                  style: 'percent',
                  maximumFractionDigits: 0,
                });
                return (
                  <>
                    <span>{val ?? '-'}</span>
                    <span className="ms-1 text-xs opacity-50">
                      (
                      {percentageFormatter.format(
                        (val ?? 0) / (attrs.visitors || 1),
                      )}
                      )
                    </span>
                  </>
                );
              }}
              showAll={false}
              fill
            />
          );
        },
      },
      {
        id: 'formSubmissions',
        header: 'Form Submissions',
        accessorKey: 'analyticsData.formSubmissions',
        size: 170,
        cell: ({ row: { original } }) => {
          const { websiteAnalytics, analyticsKey } =
            getItemMetadataCached(original);
          return (
            <StatCellValues
              analyticsType={analyticsType}
              analyticsKey={analyticsKey}
              analyticsData={websiteAnalytics}
              format={(attrs) => attrs.formSubmissions ?? '-'}
              showAll
              fill
            />
          );
        },
      },

      ...(analyticsCustomEvents?.map(
        (event) =>
          ({
            header: `Event: ${event.name}`,
            accessorKey: `analyticsData.customEvent.${event.id}`,
            size: 250,
            cell: ({ row: { original } }) => {
              const { analyticsKey, analyticsData, websiteAnalytics } =
                getItemMetadataCached(original);
              return (
                <StatCellValues
                  analyticsType={analyticsType}
                  analyticsKey={analyticsKey}
                  analyticsData={websiteAnalytics}
                  analyticsCustomEventsData={
                    analyticsData
                      ? (analyticsData?.customEvents?.[original.type]?.[
                          original.type === 'variant'
                            ? original.variant.id
                            : original.test.id
                        ] ?? {})
                      : undefined
                  }
                  analyticsCustomEvents={analyticsCustomEvents}
                  format={(attrs) => {
                    const percentageFormatter = new Intl.NumberFormat('en-US', {
                      style: 'percent',
                      maximumFractionDigits: 0,
                    });
                    return (
                      <>
                        <span>{attrs[event.id] ?? '-'}</span>
                        <span className="ms-1 text-xs opacity-50">
                          (
                          {percentageFormatter.format(
                            (attrs[event.id] ?? 0) / (attrs.visitors || 1),
                          )}
                          )
                        </span>
                      </>
                    );
                  }}
                  showAll={false}
                  fill
                />
              );
            },
          }) satisfies ColumnDef<DynamicPageVariantsRowData>,
      ) ?? []),

      ...(analyticsExternalPixelEvents?.map(
        (event) =>
          ({
            header: `Event: ${event.name}`,
            accessorKey: `analyticsData.externalPixels.${event.pixelType}`,
            size: 250,
            cell: ({ row: { original } }) => {
              const { analyticsKey, analyticsData, websiteAnalytics } =
                getItemMetadataCached(original);
              return (
                <StatCellValues
                  analyticsType={analyticsType}
                  analyticsKey={analyticsKey}
                  analyticsData={websiteAnalytics}
                  analyticsCustomEventsData={
                    analyticsData
                      ? (analyticsData?.externalPixels?.[event.pixelType]?.[
                          original.type
                        ]?.[
                          original.type === 'variant'
                            ? original.variant.id
                            : original.test.id
                        ] ?? {})
                      : undefined
                  }
                  analyticsCustomEvents={analyticsExternalPixelEvents}
                  format={(attrs) => {
                    const percentageFormatter = new Intl.NumberFormat('en-US', {
                      style: 'percent',
                      maximumFractionDigits: 0,
                    });
                    return (
                      <>
                        <span>{attrs[event.id] ?? '-'}</span>
                        <span className="ms-1 text-xs opacity-50">
                          (
                          {percentageFormatter.format(
                            (attrs[event.id] ?? 0) / (attrs.visitors || 1),
                          )}
                          )
                        </span>
                      </>
                    );
                  }}
                  showAll={false}
                  fill
                />
              );
            },
          }) satisfies ColumnDef<DynamicPageVariantsRowData>,
      ) ?? []),

      {
        header: '',
        id: 'actions',
        size: 252,
        cell: ({ row: { original } }) => (
          <VariantRowActions
            original={original}
            experimentsStats={experimentsStats}
            onOpenEditor={setOpenEditor}
            onGenerate={({
              mode,
              variantId,
              websiteId,
              controlWebsiteId,
              defaultPrompt,
            }) =>
              setVariantOriginModalState({
                open: true,
                mode,
                targetVariantId: variantId,
                targetWebsiteId: websiteId,
                controlWebsiteId,
                defaultPrompt,
              })
            }
            onEditNameAndDescription={setEditNameAndDescriptionModalOpen}
            onEditSchedule={setEditVariantScheduleModelOpen}
            onOpenExperimentModal={setExperimentModalOpen}
            onOpenAnalyticsModal={({ websiteId, variantId }) =>
              setOpenEditor({
                websiteId,
                variantId,
                initialShowAnalytics: true,
              })
            }
            onDeleteVariant={onDeleteVariantButtonClick}
            onDeleteTest={onDeleteTestButtonClick}
          />
        ),
      },
    ] satisfies ColumnDef<DynamicPageVariantsRowData>[];
  }, [
    analyticsCustomEvents,
    analyticsExternalPixelEvents,
    analyticsType,
    experimentsStats,
    getItemMetadataCached,
    onDeleteTestButtonClick,
    onDeleteVariantButtonClick,
    openEditor,
  ]);

  if (!variants || !selectedDynamicPage) {
    return (
      <div className="flex w-full justify-center pt-24">
        <Loader />
      </div>
    );
  }

  if (
    selectedDynamicPage?.data().creationStatus === 'pending' &&
    !websites?.length
  ) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="w-full max-w-md">
          {websiteBuildingAnimation && (
            <Lottie animationData={websiteBuildingAnimation} loop={true} />
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <NewEditorModal
        dynamicPageId={selectedDynamicPage.id}
        initialWebsiteId={openEditor?.websiteId || ''}
        initialVariantId={openEditor?.variantId || ''}
        initialMessage={openEditor?.initialMessage}
        initialShowAnalytics={openEditor?.initialShowAnalytics}
        open={!!openEditor}
        onClose={() => setOpenEditor(undefined)}
      />
      <EditVariantNameAndDescriptionModal
        variantId={editNameAndDescriptionModalOpen?.variantId}
        initialName={editNameAndDescriptionModalOpen?.initialName}
        initialDescription={editNameAndDescriptionModalOpen?.initialDescription}
        initialPath={editNameAndDescriptionModalOpen?.initialPath}
        open={!!editNameAndDescriptionModalOpen}
        onClose={() => setEditNameAndDescriptionModalOpen(undefined)}
      />
      <ExperimentModal
        mode={experimentModalOpen?.mode ?? 'create'}
        dynamicPageId={selectedDynamicPage.id}
        variantId={experimentModalOpen?.variantId}
        variantName={experimentModalOpen?.variantName}
        experimentId={experimentModalOpen?.experimentId}
        open={!!experimentModalOpen}
        onClose={() => setExperimentModalOpen(undefined)}
        onSuccess={() => {
          show(
            experimentModalOpen?.mode === 'edit'
              ? 'Experiment updated'
              : 'Experiment started',
            'info',
          );
        }}
      />
      <EditVariantConstraintsModal
        variantId={editVariantConstraintsModelOpen?.variantId}
        open={!!editVariantConstraintsModelOpen}
        onClose={() => setEditVariantConstraintsModelOpen(undefined)}
        updatableConstraints={[
          'keywords',
          'devices',
          'adGroups',
          'ads',
          'campaigns',
          'countries',
          'regions',
          'cities',
        ]}
      />
      <EditVariantConstraintsModal
        title="Schedule the time the variant will be active"
        subtitle="You can set the time and days of the week when this variant will be active."
        variantId={editVariantScheduleModelOpen?.variantId}
        open={!!editVariantScheduleModelOpen}
        onClose={() => setEditVariantScheduleModelOpen(undefined)}
        updatableConstraints={['schedules']}
      />
      <AlertModal
        type={'warn'}
        open={!!deleteConfirmationModalOpen}
        onClose={() => setDeleteConfirmationModalOpen(undefined)}
        title={`Are you sure you wish to delete ${deleteConfirmationModalOpen?.entity} "${deleteConfirmationModalOpen?.name}"?`}
        description="This action cannot be undone."
        actions={[
          {
            title: 'Cancel',
            type: 'secondary',
            onClick: () => setDeleteConfirmationModalOpen(undefined),
          },
          {
            title: 'Delete',
            type: 'primary',
            color: 'negative',
            onClick: async () => {
              const sb = show('Deleting...', 'info', {
                disableAutoClose: true,
              });
              await deleteConfirmationModalOpen
                ?.onConfirm()
                ?.then(() => {
                  show('Deleted', 'info');
                })
                ?.catch((e) => {
                  console.error(e);
                  show('Failed to delete', 'warn');
                  Sentry.captureException(e);
                })
                .finally(() => {
                  hide(sb.id);
                });

              setDeleteConfirmationModalOpen(undefined);
            },
          },
        ]}
      />
      <VariantOriginModal
        open={variantOriginModalState.open}
        onClose={() =>
          setVariantOriginModalState((prev) => ({ ...prev, open: false }))
        }
        onSelect={handleVariantOriginSelect}
        mode={variantOriginModalState.mode}
        title={
          variantOriginModalState.mode === 'test'
            ? 'Generate experiment variation'
            : 'Create a new variant'
        }
        subtitle={
          variantOriginModalState.mode === 'test'
            ? 'Choose how you want to create this variation'
            : 'Choose how you want to start your new variant'
        }
        currentPageVariants={variants?.map((v) => ({
          id: v.id,
          name: v.data().name || 'Untitled Variant',
          description: v.data().description,
          status: v.data().status || 'draft',
          websiteIds: v.data().websiteIds || [],
        }))}
        currentDynamicPageId={selectedDynamicPage?.id}
        currentDynamicPageName={selectedDynamicPage?.data().name || ''}
        allDynamicPages={dynamicPagesWithVariants}
        excludeDynamicPageId={selectedDynamicPage?.id}
        excludeVariantId={variantOriginModalState.targetVariantId}
        disabled={isLoadingDynamicPages || isCreatingVariant}
        controlVariation={
          variantOriginModalState.mode === 'test' &&
          variantOriginModalState.controlWebsiteId &&
          variantOriginModalState.targetVariantId
            ? {
                websiteId: variantOriginModalState.controlWebsiteId,
                variantId: variantOriginModalState.targetVariantId,
                variantName:
                  variants
                    ?.find(
                      (v) => v.id === variantOriginModalState.targetVariantId,
                    )
                    ?.data().name || 'Untitled Variant',
              }
            : undefined
        }
        defaultPrompt={variantOriginModalState.defaultPrompt ?? undefined}
      />

      {!openEditor && (
        <>
          <div className="mt-6 flex w-full flex-col">
            <div className="flex flex-col items-stretch justify-between gap-3 pb-0 lg:flex-row lg:items-center"></div>
            <DynamicStats
              stats={[...stats, ...adAccountStats]}
              className="font-inter mt-0 overflow-x-auto"
            />
            <div className="bg-base-200 mt-6 h-px w-full"></div>

            {/* {adAccountStats && <StatsGrid stats={adAccountStats} />} */}

            <div className="inline-block min-w-full py-8 pt-6 pb-6 align-middle">
              <div className="font-inter flex items-center justify-between pb-4">
                <h1 className="text-lg leading-6 font-medium text-gray-900">
                  Variants
                </h1>
                <div className="flex items-center gap-2.5">
                  {tableRef.current?.table && (
                    <ColumnManagementPopover
                      table={tableRef.current.table}
                      trigger={
                        <Button variant="outline" size={'sm'}>
                          Manage Columns
                        </Button>
                      }
                    />
                  )}
                  <Button
                    variant="default"
                    size={'sm'}
                    onClick={() => {
                      setVariantOriginModalState({
                        open: true,
                        mode: 'variant',
                      });
                    }}
                  >
                    <Sparks className="h-4 w-4" />
                    New Variant
                  </Button>
                </div>
              </div>
              <CustomDataGrid
                ref={tableRef}
                logicalName="DynamicPageVariants"
                columns={columns}
                data={rows}
                virtualized={false}
                rowClassName={(row) => {
                  // Find the index of this row in the rows array
                  const currentIndex = rows.findIndex(
                    (r) =>
                      r.type === row.original.type &&
                      (r.type === 'test'
                        ? r.test.id === row.original.test.id
                        : r.variant.id === row.original.variant.id),
                  );

                  if (row.original.type === 'test') {
                    // Check if the next row is also a test from the same variant
                    const nextRow = rows[currentIndex + 1];
                    const isNextRowAlsoTest =
                      nextRow &&
                      nextRow.type === 'test' &&
                      nextRow.variant.id === row.original.variant.id;

                    return `bg-white hover:bg-gray-100 py-0 rounded-lg ${isNextRowAlsoTest ? 'border-b-0' : ''}`;
                  } else if (row.original.type === 'variant') {
                    // Check if the next row is a test from this variant
                    const nextRow = rows[currentIndex + 1];
                    const isNextRowTest =
                      nextRow &&
                      nextRow.type === 'test' &&
                      nextRow.variant.id === row.original.variant.id;

                    return `${isNextRowTest ? 'border-b-0' : ''}`;
                  }

                  return '';
                }}
                cellClassName="font-inter"
                initialState={{
                  columnPinning: {
                    right: ['actions'],
                  },
                  columnVisibility: {
                    itemId: false,
                    buttonClicks: false,
                    formSubmissions: false,
                    uniqueLinkClicks: false,
                  },
                }}
              />
              {/* {selectedDynamicPage?.data().creationStatus === 'pending' ? (
            <div className="flex w-full justify-center pt-12">
              <Loader />
            </div>
          ) : (
            <></>
          )} */}
            </div>
          </div>
        </>
      )}
    </>
  );
}

interface OpenInNewWindowButtonMenuProps {
  variantId: string;
  websiteId: string;
}

const OpenInNewWindowButtonMenu = ({
  variantId,
  websiteId,
}: OpenInNewWindowButtonMenuProps) => {
  const { selectedAccount } = useUserContext();
  const { selectedDynamicPage, primaryDomain } = useDynamicPageContext();

  const [productionAlertOpen, setProductionAlertOpen] = useState<
    'not-live' | 'unpublished-changes' | null
  >(null);

  const isLive = selectedDynamicPage?.data().isLive;
  const hasChangesToPublish = selectedDynamicPage?.data().hasChangesToPublish;

  const openProductionUrl = () => {
    window.open(
      `https://${primaryDomain?.data().domain}/${
        selectedDynamicPage?.data().path
      }?utm=test_${websiteId}&sf_env=test`,
      '_blank',
    );
  };

  const handleOpenInProductionClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    if (!isLive) {
      setProductionAlertOpen('not-live');
      return;
    }

    if (hasChangesToPublish) {
      setProductionAlertOpen('unpublished-changes');
      return;
    }

    openProductionUrl();
  };

  return (
    <>
      <AlertModal
        type="warn"
        open={productionAlertOpen === 'not-live'}
        onClose={() => setProductionAlertOpen(null)}
        title="Page not published"
        description="This page has never been published. The production link will not work until you publish the page first."
        actions={[
          {
            title: 'Close',
            type: 'secondary',
            onClick: () => setProductionAlertOpen(null),
          },
        ]}
      />
      <AlertModal
        type="warn"
        open={productionAlertOpen === 'unpublished-changes'}
        onClose={() => setProductionAlertOpen(null)}
        title="Unpublished changes"
        description="You have unpublished changes. The production page may not reflect your latest edits. Are you sure you want to open it?"
        actions={[
          {
            title: 'Cancel',
            type: 'secondary',
            onClick: () => setProductionAlertOpen(null),
          },
          {
            title: 'Open anyway',
            type: 'primary',
            onClick: () => {
              setProductionAlertOpen(null);
              openProductionUrl();
            },
          },
        ]}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size={'sm'}
            variant="ghost"
            className="hover:bg-base-200 -ms-1.5"
          >
            <OpenNewWindow className="text-base-400 size-4.5" />
            <span className="sr-only">, Visit</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" side="top">
          <DropdownMenuItem
            onClick={(e) => {
              if (!selectedAccount || !selectedDynamicPage || !primaryDomain) {
                return;
              }

              e.stopPropagation();

              const initialUrl = buildPreviewUrl(
                primaryDomain.data().domain,
                selectedAccount.id,
                selectedDynamicPage.id,
                variantId,
                websiteId,
                '',
                false,
              );

              window.open(initialUrl, '_blank');
            }}
          >
            <div className="flex flex-row items-center gap-2">
              <Link className="h-5 w-5 rotate-135" />
              <div className="flex flex-col">
                <div>Open in staging</div>
                <div className="text-base-500 text-xs">
                  the version before publishing
                </div>
              </div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleOpenInProductionClick}>
            <Link className="h-5 w-5 rotate-135" />
            Open in production
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
